import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Building2 } from 'lucide-react'
import { ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { OffsetPager } from '@/shared/ui/CursorPager'
import { FilterBar, SearchInput } from '@/shared/ui/filters'
import { EmptyState, ErrorStrip, Forbidden, LoadingRows } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { usePoliticsCodes, CODE_LIST_UNAVAILABLE } from '@/features/polling/useVocabulary'
import { fmtDate } from '@/shared/lib/format'
import { listParties } from './api'
import { PartyDrawer } from './PartyDrawer'
import { PartyFormDialog } from './PartyFormDialog'
import {
  EntityChip, Pill, Swatch, count, partyStatusLabel, partyStatusTone,
} from './vocab'

const PAGE_SIZE = 50

/**
 * The party register.
 *
 * ## The coalition column is a fact about the party, not a lookup
 *
 * `PartyResponse.coalition` is the party's OPEN membership, sent with the row —
 * so the chip needs no second request and cannot disagree with the drawer. A
 * party with none is not blank: "Independent of any coalition" is a statement,
 * and an empty cell reads as missing data.
 *
 * ## Filters go through the server
 *
 * `search` and `status` are query parameters, not a client-side `.filter()` over
 * one page. Filtering the 50 rows already fetched would silently mean "search the
 * first page", which is the defect this port exists to remove.
 */
export function PartiesPage() {
  const canRead = useCan('politics:read')
  const canWrite = useCan('politics:party')

  const [params, setParams] = useSearchParams()
  // The code list is the server's; `vocab.tsx` only owns the English.
  const statuses = usePoliticsCodes('partyStatus')
  const q = params.get('q') ?? ''
  const status = params.get('status') ?? ''
  const page = Number(params.get('page') ?? 0)
  const openId = params.get('party')

  const [creating, setCreating] = useState(false)

  const list = useLoad(
    () => listParties({ search: q || undefined, status: status || undefined }, page, PAGE_SIZE),
    [q, status, page])

  if (!canRead) return <Forbidden what="the party register" />

  /** One writer for the URL, so a filter change always resets the page. */
  function patch(next: Record<string, string>) {
    const sp = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      // Page zero is the default, so it is absent from the URL rather than
      // spelled out — a shared link should not carry `?page=0`.
      if (v && !(k === 'page' && v === '0')) sp.set(k, v)
      else sp.delete(k)
    }
    if (!('page' in next)) sp.delete('page')
    setParams(sp, { replace: true })
  }

  const rows = list.data?.content ?? []
  const filtered = Boolean(q || status)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Parties"
        description="Who is registered, under whose banner, and with how many candidates."
        right={canWrite && (
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            onClick={() => setCreating(true)}>
            New party
          </button>
        )}
      />

      <FilterBar activeCount={(q ? 1 : 0) + (status ? 1 : 0)}>
        <SearchInput value={q} onChange={(v) => patch({ q: v })}
          placeholder="Name or code…" />
        <select className="erp-input axis-full-sm" aria-label="Status" value={status}
          onChange={(e) => patch({ status: e.target.value })}>
          <option value="">Any status</option>
          {/* The served code list, never a literal written here: a transcribed
              list drifts silently the day the server adds a seventh status. A
              status outside it still renders on the row; only this filter would
              miss it, and `unavailable` says so below. */}
          {statuses.codes.map((s) => (
            <option key={s} value={s}>{partyStatusLabel(s)}</option>
          ))}
        </select>
        {statuses.unavailable && (
          <span className="text-erp-xs text-red-700">{CODE_LIST_UNAVAILABLE}</span>
        )}
        {filtered && (
          <button type="button" className="erp-btn-ghost h-9 text-erp-sm"
            onClick={() => patch({ q: '', status: '' })}>
            Clear filters
          </button>
        )}
      </FilterBar>

      <Panel bodyClassName="p-0">
        <ErrorStrip message={list.error} />
        <ErpTable head={(
          <>
            <Th>Code</Th>
            <Th>Name</Th>
            <Th className="axis-hide-sm">Short name</Th>
            <Th>Coalition</Th>
            <Th className="text-right">Candidates</Th>
            <Th>Status</Th>
            <Th className="axis-hide-sm">Registered</Th>
          </>
        )}>
          {list.loading && rows.length === 0 ? (
            <LoadingRows colSpan={7} label="Loading the register…" />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={7}>
                <EmptyState
                  icon={<Building2 className="h-4 w-4" />}
                  title={filtered ? 'No party matches this filter' : 'No parties yet'}
                  hint={filtered
                    ? 'Clear a filter to widen the search.'
                    : 'The register starts empty. Everything else here — coalitions, candidates, '
                      + 'the colours on every chart — hangs off a party.'}
                  action={filtered
                    ? (
                      <button type="button" className="erp-btn"
                        onClick={() => patch({ q: '', status: '' })}>Clear filters</button>
                    ) : canWrite ? (
                      <button type="button" className="erp-btn erp-btn-primary"
                        onClick={() => setCreating(true)}>Add the first party</button>
                    ) : undefined}
                />
              </td>
            </tr>
          ) : rows.map((p) => (
            <tr
              key={p.id}
              className="hover:bg-muted/40 cursor-pointer"
              tabIndex={0}
              onClick={() => patch({ party: p.id, page: String(page) })}
              onKeyDown={(e) => { if (e.key === 'Enter') patch({ party: p.id, page: String(page) }) }}
            >
              <Td>
                <span className="inline-flex items-center gap-2">
                  <Swatch colourHex={p.colourHex} />
                  <span className="font-mono text-erp-xs">{p.code}</span>
                </span>
              </Td>
              <Td className="font-medium">{p.name}</Td>
              <Td className="axis-hide-sm text-muted-foreground">{p.shortName ?? '—'}</Td>
              <Td>
                {p.coalition
                  ? (
                    <EntityChip
                      entity={{
                        code: p.coalition.coalitionCode,
                        name: p.coalition.coalitionName,
                        colourHex: p.coalition.coalitionColourHex,
                      }}
                    />
                  )
                  : (
                    <span className="text-erp-xs text-muted-foreground">
                      Independent of any coalition
                    </span>
                  )}
              </Td>
              <Td className="text-right tabular-nums">{count(p.candidateCount)}</Td>
              <Td>
                <Pill tone={partyStatusTone(p.statusCode)}>{partyStatusLabel(p.statusCode)}</Pill>
              </Td>
              <Td className="axis-hide-sm whitespace-nowrap tabular-nums text-muted-foreground">
                {fmtDate(p.registeredOn)}
              </Td>
            </tr>
          ))}
        </ErpTable>

        <OffsetPager
          page={page}
          totalPages={list.data?.totalPages ?? 1}
          totalElements={list.data?.totalElements ?? 0}
          onPage={(next) => patch({ page: String(next) })}
          loading={list.loading}
          unit="parties"
        />
      </Panel>

      {/*
        * KEYED BY THE ROW, so React remounts it instead of reusing it.
        *
        * A drawer that stays mounted across rows keeps every piece of local
        * state from the last one: a half-open form, a typed value, and — worst —
        * the previous row's error. Opening party B after a refusal on party A
        * showed A's refusal under B's name, and the "Join" button toggled a form
        * that was already open from A, so it appeared to do nothing. One line
        * here is cheaper and more reliable than an effect that resets six
        * useStates and has to be extended every time a seventh is added.
        */}
      <PartyDrawer key={openId ?? 'none'}
        partyId={openId}
        onClose={() => patch({ party: '', page: String(page) })}
        onChanged={list.reload}
      />

      <PartyFormDialog
        open={creating}
        party={null}
        onClose={() => setCreating(false)}
        onSaved={(saved) => {
          setCreating(false)
          list.reload()
          // Straight into the new party's panel: registering one is almost always
          // the first half of "and put it in a coalition".
          patch({ party: saved.id })
        }}
      />
    </div>
  )
}
